import React, { useState, useEffect, Fragment } from 'react';
import { flatMap } from '../../../../../Util';
import PanelManualSortingBody from '../PanelManualSortingBody';
import SearchProducts from '../../SearchProducts';
import { pinId } from '../../../Ranking/utils';
import { ShopifyManualSortingProductItem } from './ShopifyManualSortingProductItem';
import { PinnedShopifyProduct, ShopifyCollectionWithPinnedProducts } from './ShopifyManualSortingTypes';
import { ProductRequestResult, ProductRequestResultProduct } from '../../../../../Services/SearchProductFeedTypes';
import { List } from '../../../../../Components/List';
import { ShopifyProductCollection } from '../../../../../Services/ShopifyManualSortingServices';

type ShopifyManualSortingPanelBodyType = {
	collectionWithPinnedProducts: ShopifyCollectionWithPinnedProducts[],
	shopifyCollections: ShopifyProductCollection[] | undefined,
	searchTerm: string,
	searchTermForInput: string,
	searchProductsByTerm: (callbackSuccess: ((data: ProductRequestResult) => void), callbackError?: ((error: string) => void)) => void,
	handleAutocompleteChange,
	handleAdd,
	handleDeleteAll,
	handleAddSearchProduct: (product: ProductRequestResultProduct) => void,
	handleDeleteProductPosition: (collectionId: string, selectedProduct: PinnedShopifyProduct) => void,
	handleEditProductPosition: (collectionId: string, selectedProduct: PinnedShopifyProduct) => void,
	hasCollisionWithTransformations: (item: PinnedShopifyProduct) => { hasEror: boolean, message?: string | null }[]
}

export default function ShopifyManualSortingPanelBody({
	collectionWithPinnedProducts,
	shopifyCollections,
	searchTerm,
	searchTermForInput,
	searchProductsByTerm,
	handleAutocompleteChange,
	handleAdd,
	handleDeleteAll,
	handleDeleteProductPosition,
	handleEditProductPosition,
	handleAddSearchProduct,
	hasCollisionWithTransformations
}: ShopifyManualSortingPanelBodyType) {

	const [filteredItems, setFilteredItems] = useState<ShopifyCollectionWithPinnedProducts[]>(collectionWithPinnedProducts);

	useEffect(() => {
		if (searchTerm) {

			const filtered = collectionWithPinnedProducts
				.map(x => ({
					...x, pinnedProducts: x.pinnedProducts.filter(item =>
						item.product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
						item.product.id.toLowerCase().includes(searchTerm.toLowerCase())
					)
				}))
				.filter(x => x.pinnedProducts.length > 0);

			setFilteredItems(filtered);

		} else {
			setFilteredItems(collectionWithPinnedProducts);
		}
	}, [searchTerm, collectionWithPinnedProducts]);


	const hasError = (pinnedProducts: PinnedShopifyProduct[], item: PinnedShopifyProduct): { hasEror: boolean, message?: string | null } => {
		const hasPositionError = (): { hasEror: boolean, message: string | null } => {
			const otherProductOnSamePosition = pinnedProducts.find(x => x.product.id !== item.product.id && x.position === item.position);
			const message = otherProductOnSamePosition !== undefined ? 'Another product has same position: ' + otherProductOnSamePosition.product.id : null;
			return { hasEror: otherProductOnSamePosition !== undefined, message };
		};

		const posError = hasPositionError();
		if (posError.hasEror)
			return posError;

		const collisionError = hasCollisionWithTransformations(item).find(x => x.hasEror);
		if (collisionError?.hasEror) {
			return collisionError;
		}

		return { hasEror: false };
	};

	const productsList = (c: ShopifyCollectionWithPinnedProducts) =>
		<Fragment key={c.collectionId}>
			<div className='fw_medium'>{c.label ?? c.collectionId}</div>
			<List size="s" className="mb_20">
				{c.pinnedProducts.map((item) => (
					<ShopifyManualSortingProductItem
						key={item.product.id}
						onEdit={() => handleEditProductPosition(c.collectionId, item)}
						onDelete={() => handleDeleteProductPosition(c.collectionId, item)}
						item={item}
						error={hasError(c.pinnedProducts, item)}
					/>
				))}
			</List>
		</Fragment>;

	const collectionList = () => filteredItems.map(c => productsList(c));

	const takeOnlyProductsWithManualShopifyCollection = (products: ProductRequestResultProduct[]): ProductRequestResultProduct[] => {
		const collections = shopifyCollections?.map(x => x.handle) ?? [];
		return products.filter(x => x.categories?.find(c => c._type == 'collection' && collections.includes(c._name ?? '')));
	};

	return (
		<PanelManualSortingBody
			searchTerm={searchTerm}
			searchTermForInput={searchTermForInput}
			handleSearch={handleAutocompleteChange}
			handleDeleteAll={handleDeleteAll}
			nbProducts={filteredItems ? flatMap(filteredItems, x => x.pinnedProducts.map(p => p.product)).length : 0}
			dimension={pinId}
			handleAdd={handleAdd}
			productList={
				<>
					{collectionList()}
					<SearchProducts
						dimension={pinId}
						onAddProduct={(p: ProductRequestResultProduct) => handleAddSearchProduct(p)}
						searchTerm={searchTerm}
						searchProductsByTerm={(onSuccess, onError) => searchProductsByTerm(data => {
							onSuccess({ ...data, products: takeOnlyProductsWithManualShopifyCollection(data.products) });
						}, onError)}
						arrayOfIdsToExcludes={flatMap(collectionWithPinnedProducts, x => x.pinnedProducts.map(p => p.product.id))}
						allProduct={true}
						deleteList={undefined} // ----- ?
					/>
				</>
			}
		/>
	);
}
